import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AppleIcon, ChevronDownIcon } from 'lucide-react';
import { appStoreListings } from '../../data/appTour';
type Device = 'ios' | 'android';
interface DeviceInfo {
  isMobile: boolean;
  initial: Device;
}
function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isAndroid = /Android/.test(ua);
  return {
    isMobile: isIOS || isAndroid,
    initial: isAndroid ? 'android' : 'ios'
  };
}
const devices: {
  id: Device;
  label: string;
}[] = [{
  id: 'ios',
  label: 'iPhone / iPad'
}, {
  id: 'android',
  label: 'Android'
}];
function GooglePlayMark({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor" data-id="element-469" data-mp="1ynbed7tcvirx">
      <path d="M4.2 2.3a1.3 1.3 0 0 0-.5 1.1v17.2c0 .5.2.9.5 1.1l9.6-9.7-9.6-9.7Zm10.7 8.6 2.7-2.7L6.1 1.7c-.4-.2-.8-.3-1.1-.2l9.9 9.4Zm0 2.2L5 22.5c.3.1.7 0 1.1-.2l11.5-6.5-2.7-2.7Zm6.2-2.4-2.4-1.4-3 2.7 3 2.7 2.4-1.4c.9-.5.9-2.1 0-2.6Z" data-id="element-470" data-mp="1toq8osvc46w3" />
    </svg>;
}

/**
 * Device chooser with a store button and a QR code that points directly at the
 * store listing. On phones the direct button leads; the QR code is tucked away
 * for scanning from another device.
 */
interface AppDownloadChooserProps {
  /** Hide the intro sentence when the surrounding page already says it. */
  hideDescription?: boolean;
}
export function AppDownloadChooser({
  hideDescription = false
}: AppDownloadChooserProps) {
  const [{
    isMobile,
    initial
  }] = useState(detectDevice);
  const [device, setDevice] = useState<Device>(initial);
  const [showQr, setShowQr] = useState(false);
  const url = appStoreListings[device];
  const deviceName = device === 'ios' ? 'iPhone or iPad' : 'Android phone';
  const storeButton = <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl bg-charcoal px-6 py-3.5 text-white transition-colors duration-200 ease-out hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white" data-id="element-471" data-mp="2gz71iecbkevn">
      {device === 'ios' ? <AppleIcon aria-hidden="true" className="w-6 h-6" data-id="element-472" data-mp="3p84kb76abmah" /> : <GooglePlayMark className="w-5 h-5" data-id="element-473" data-mp="73b0wxubyv9y" />}
      <span className="text-left leading-tight" data-id="element-474" data-mp="71q9vi785ugu">
        <span className="block text-[11px] text-white/70" data-id="element-475" data-mp="24ztsuvf6yvf">
          {device === 'ios' ? 'Download on the' : 'Get it on'}
        </span>
        <span className="block text-base font-semibold" data-id="element-476" data-mp="gsunadh9579k">
          {device === 'ios' ? 'App Store' : 'Google Play'}
        </span>
      </span>
    </a>;
  const qr = <figure className="flex flex-col items-center" data-id="element-477" data-mp="ptapljm9e8cu">
      <div className="rounded-2xl border border-purple-200 bg-white p-4" data-id="element-478" data-mp="prznrpcdjaod">
        <QRCodeSVG value={url} size={176} level="M" fgColor="#1f1f1f" bgColor="#ffffff" title={`QR code to download Fem Entity for ${deviceName}`} data-id="element-479" data-mp="kwk94v6jlaqw" />
      </div>
      <figcaption className="mt-3 text-sm text-charcoal/65 text-center" data-id="element-480" data-mp="19dhyfcyfx2rr">
        Scan with your {deviceName} camera
      </figcaption>
    </figure>;
  return <div className="rounded-3xl border border-purple-200 bg-white px-6 py-8 md:px-9 md:py-9 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-481" data-mp="zgd7z0xkal55">
      <h3 className="font-serif text-2xl text-charcoal" data-id="element-482" data-mp="logjd6v3qtdi">Continue in the Fem Entity app</h3>
      {!hideDescription && <p className="mt-3 text-base text-charcoal/70 leading-relaxed" data-id="element-483" data-mp="bslxm2vl0i6i">
          Choose your device to download Fem Entity. Then sign in with the account you just created to complete your
          practitioner profile and verification.
        </p>}

      <div role="group" aria-label="Choose your device" className="mt-7 grid grid-cols-2 gap-1 rounded-full bg-purple-50 p-1" data-id="element-484" data-mp="2s76unzumavm">
        {devices.map(option => {
        const active = option.id === device;
        return <button key={option.id} type="button" onClick={() => {
          setDevice(option.id);
          setShowQr(false);
        }} aria-pressed={active} className={`whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 ${active ? 'bg-purple-700 text-white' : 'text-charcoal/70 hover:text-purple-800'}`} data-id="element-485" data-mp="3qwdocb3pf5g1">
              {option.label}
            </button>;
      })}
      </div>

      {isMobile ? <div className="mt-8" data-id="element-486" data-mp="2z70o8r43ctps">
          {storeButton}
          <button type="button" onClick={() => setShowQr(v => !v)} aria-expanded={showQr} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-487" data-mp="11e4xv6nviyl6">
            Show QR code for another device
            <ChevronDownIcon aria-hidden="true" className={`w-4 h-4 transition-transform duration-200 ease-out ${showQr ? 'rotate-180' : ''}`} data-id="element-488" data-mp="2p19agejukz2s" />
          </button>
          {showQr && <div className="mt-5" data-id="element-489" data-mp="1wd3nrm98h00b">{qr}</div>}
        </div> : <div className="mt-8 flex flex-col items-center gap-6" data-id="element-490" data-mp="1weof730942ty">
          {qr}
          <div className="flex w-full items-center gap-3 text-xs text-charcoal/45" data-id="element-491" data-mp="21diz689yfxfn">
            <span className="h-px flex-1 bg-charcoal/10" data-id="element-492" data-mp="25bgr3iv40xmq" />
            or open on this device
            <span className="h-px flex-1 bg-charcoal/10" data-id="element-493" data-mp="2ado6yjj28xuc" />
          </div>
          {storeButton}
        </div>}
    </div>;
}